/**
 * Pooled shelf-crop annotator — labels live in Postgres, crops stream from S3.
 *
 * The shelf sibling of PooledTableAnnotator, and it inherits everything that made that
 * one work: no download step, server-side paging, facet counts that always agree with
 * the grid, and stickiness — labelling does NOT refetch, so a crop you just judged stays
 * where it is and a misclick is fixable on the spot.
 *
 * What is different is the label itself. A shelf has ONE value, fullness in percent, so
 * there is no stage funnel: the six views partition the zone directly. Fullness is
 * entered in 5% steps, because finer than that is not a judgement a human makes reliably
 * from a crop — and the extra precision would be noise dressed as signal.
 *
 * The rule that governs every branch here: **null is not 0**. `null` means nobody has
 * looked; `0` means the shelf is empty, which is the case the alert exists for. They are
 * rendered differently, counted differently, and `?? 0` never appears.
 */
import { Select } from '@/components/ui/select';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import useSWR from 'swr';
import {
  Loader2, Trash2, RefreshCw, ZoomIn, ZoomOut, ChevronLeft, ChevronRight, Play, Layers,
  Sparkles,
  ArrowDownUp, Eye, EyeOff,
} from 'lucide-react';
import { useT } from '@/contexts/I18nContext';
import SamPromptBench from './SamPromptBench';
import ShelfMaskEditor from './ShelfMaskEditor';
import { CropWithUsefulArea } from './UsefulAreaOverlay';
import {
  getShelfPoolSamples, getShelfPoolZones, getShelfTrainStatus, shelfPoolAnnotate,
  shelfPoolDelete, shelfPoolRetrain, getShelfCollectPrecheck, getShelfCollectRuns,
  launchShelfCollect, launchShelfPrelabel, PERCENT_STEP, VIEW_ROWS, viewParams,
  type ShelfPoolItem, type Sort, type View,
} from '@/api/shelfPool';
import { applyShelfDelta } from './applyShelfDelta';
import CollectCropsDialog, { type CollectApi } from './CollectCropsDialog';
import { Button } from '@/components/ui/button';
import { Tooltip } from '@/components/ui/tooltip';
import { unlinkCollection } from '@/api/collections';
import { CollectionRemoveDialog } from './CollectionFilter';

/** Endpoints the shared «Собрать кропы» dialog calls for shelves. */
const SHELF_COLLECT_API: CollectApi = {
  precheck: getShelfCollectPrecheck,
  launch: launchShelfCollect,
  runs: getShelfCollectRuns,
};

const USEFUL_AREA_KEY = 'shelfPool.showUsefulArea';
const MIN_COLS = 2;
const MAX_COLS = 8;
const PAGE_SIZE = 200;

/** i18n key for a view (the dictionary predates the BE's British spelling). */
const viewKey = (v: View) => (v === 'unlabelled' ? 'unlabeled' : v);

/** Keep a value legal. Nothing more: an AI verdict is a measurement with a decimal
 *  (SAM writes 37.1, not 35), and confirming it must not quietly move it. */
const boundPct = (v: number) => Math.min(100, Math.max(0, v));

/** Snap to the 5% grid. Only for keystrokes a HUMAN makes — the grid is what makes
 *  +/- feel like steps — never for a value that is already someone's answer. */
const snapPct = (v: number) => boundPct(Math.round(v / PERCENT_STEP) * PERCENT_STEP);

/** Colour by fullness — the same reading the operator's rack status uses, so a labeller
 *  glancing at the grid sees the empty end standing out rather than having to read
 *  numbers. Deliberately NOT applied to an unjudged crop. */
const pctTone = (p: number) =>
  p <= 20 ? 'bg-rose-100 text-rose-900'
    : p <= 50 ? 'bg-amber-100 text-amber-900'
      : p <= 80 ? 'bg-lime-100 text-lime-900'
        : 'bg-emerald-100 text-emerald-900';

const pctOutline = (p: number | null | undefined) => {
  if (p == null) return 'outline outline-4 outline-slate-300';
  if (p <= 20) return 'outline outline-4 outline-rose-500';
  if (p <= 50) return 'outline outline-4 outline-amber-500';
  if (p <= 80) return 'outline outline-4 outline-lime-500';
  return 'outline outline-4 outline-emerald-500';
};

export default function PooledShelfAnnotator() {
  const { t } = useT();
  const [searchParams, setSearchParams] = useSearchParams();
  const collection = searchParams.get('collection') || '';
  const [pendingRemoval, setPendingRemoval] = useState<ShelfPoolItem | null>(null);
  const [zoneId, setZoneId] = useState<number | null>(null);
  const [view, setView] = useState<View>(() => {
    const requested = searchParams.get('view') as View;
    return VIEW_ROWS.flat().includes(requested) ? requested : 'unlabelled';
  });
  // Only crops with objects, and only among LABELLED ones. The unlabelled queue keeps
  // the old percentage UI: those crops have no masks to edit, and hiding them would hide
  // the actual backlog.
  const [hasMasks, setHasMasks] = useState(true);
  const [prelabelling, setPrelabelling] = useState(false);
  const [batchSize, setBatchSize] = useState(100);
  const [batch, setBatch] = useState(0);
  // Deeplink list (Алексей 19.10, AI282): `/admin/data?tab=pool-shelf&ids=1,2,3` opens
  // exactly these crops in this order — the label audit hands out such links so a
  // labeller lands on the crop instead of hunting for it by zone and shelf. The list
  // replaces the zone picker the way a batch does; leaving it clears the address.
  const [listIds, setListIds] = useState<number[]>(() =>
    (searchParams.get('ids') || '').split(',').map(Number).filter(n => Number.isInteger(n) && n > 0));
  const exitList = () => changeFilter(() => {
    setListIds([]);
    setSearchParams(prev => { const next = new URLSearchParams(prev); next.delete('ids'); return next; }, { replace: true });
  });
  const [sort, setSort] = useState<Sort>('');
  const { labelled, origin } = viewParams(view);
  const [shelfId, setShelfId] = useState<number | null>(null);
  const [page, setPage] = useState(1);
  const [cols, setCols] = useState(3);
  // Серый слой полезной площади полки на кропах (Дмитрий 19.10). Включён по умолчанию,
  // выбор помнится в браузере: размечающий решает один раз, а не на каждой странице.
  const [showUseful, setShowUseful] = useState<boolean>(() => {
    try { return localStorage.getItem(USEFUL_AREA_KEY) !== '0'; } catch { return true; }
  });
  const toggleUseful = () => setShowUseful(v => {
    try { localStorage.setItem(USEFUL_AREA_KEY, v ? '0' : '1'); } catch { /* приватное окно */ }
    return !v;
  });
  const [focus, setFocus] = useState(0);
  const [saved, setSaved] = useState('');
  // Local pending values. `undefined` = untouched this session; `null` = retracted.
  const [vals, setVals] = useState<Record<number, number | null>>({});
  const [touched, setTouched] = useState<Set<number>>(new Set());
  const [removed, setRemoved] = useState<Set<number>>(new Set());
  const [busy, setBusy] = useState(false);

  const { data: zonesResp, mutate: reloadZones } = useSWR('shelf-pool-zones',
    () => getShelfPoolZones().then(r => r.data));
  const zones = zonesResp?.zones ?? [];
  useEffect(() => {
    if (!collection && zoneId == null && zones.length) setZoneId(zones[0].zone_id);
  }, [zones, zoneId, collection]);

  const { data: train, mutate: mutateTrain } = useSWR(
    'shelf-pool-train-status', () => getShelfTrainStatus().then(r => r.data),
    { revalidateOnFocus: true });

  const key = (zoneId == null && !batch && !listIds.length && !collection) ? null
    : ['shelf-pool', listIds.length ? 'list' : batch ? 'batch' : zoneId, view, batch, sort, shelfId ?? 0, page,
       hasMasks, listIds.join(','), collection] as const;
  const { data, isLoading, mutate } = useSWR(
    key,
    () => getShelfPoolSamples({ collection, zone_id: zoneId, labelled, origin, batch: collection ? 0 : batch, sort, ids: collection ? [] : listIds,
      shelf_id: batch || listIds.length ? null : shelfId, page, page_size: PAGE_SIZE,
      // Always report the toggle, whatever view is open. Suppressing it here meant the
      // chips could not be narrowed while standing on the unlabelled queue, so "labelled
      // by AI (607)" sat next to a list that would show none. The server owns the one
      // exception — it never narrows the unlabelled queue itself.
      has_masks: hasMasks }).then(r => r.data),
    { revalidateOnFocus: false, keepPreviousData: true },
  );

  // Seed local values from the page. Rows already touched this session keep their local
  // value so a pending (debounced) edit is never overwritten by a stale server copy.
  const rows = data?.items;
  useEffect(() => {
    if (!rows) return;
    setVals(prev => {
      const next = { ...prev };
      for (const it of rows) if (!touched.has(it.id)) next[it.id] = it.percentage;
      return next;
    });
  }, [rows, touched]);
  // Focus belongs to the PAGE, not to the values: resetting it when a row is first
  // touched would drag the cursor back to the first card mid-run.
  //
  // Keyed on the QUERY, not on `rows`. Saving a crop patches the cached response in
  // place, which is a new items array and therefore a new `rows` identity — so a reset
  // keyed on `rows` fired on every save and threw the cursor back to the first card,
  // exactly the mid-run drag this reset was written to avoid. These seven values are
  // what the labeller actually navigates; nothing else should move the cursor.
  useEffect(() => { setFocus(0); },
    [zoneId, view, batch, sort, shelfId, page, hasMasks, listIds]);

  const countFor = (v: View) =>
    (v === 'cancelled' || v === 'collected'
      ? data?.facets.unlabelled_origins?.[v]
      : data?.facets.labelled?.[v]) ?? 0;


  const items: ShelfPoolItem[] = useMemo(
    () => (data?.items ?? []).filter(i => !removed.has(i.id)), [data, removed]);

  // ── debounced save ─────────────────────────────────────────────────────────
  const dirty = useRef<Set<number>>(new Set());
  const valsRef = useRef(vals); valsRef.current = vals;
  const viewRef = useRef(view); viewRef.current = view;
  const accounted = useRef<Set<number>>(new Set());
  const timer = useRef<number | null>(null);

  const flush = useCallback(async () => {
    if (timer.current) { clearTimeout(timer.current); timer.current = null; }
    if (!dirty.current.size) return;
    const ids = [...dirty.current];
    dirty.current.clear();
    const written = new Map<number, number | null>();
    const payload = ids.map(id => {
      const p = valsRef.current[id];
      // Sent as held. This used to re-snap on the way out, which silently rewrote every
      // confirmed AI verdict to the nearest 5% — the labeller agreed with 37.1 and the
      // pool recorded 35.
      const value = p == null ? null : boundPct(p);
      written.set(id, value);
      return { id, percentage: value };
    });
    try {
      const r = await shelfPoolAnnotate(payload);
      const fresh = new Map([...written].filter(([id]) => !accounted.current.has(id)));
      ids.forEach(id => accounted.current.add(id));
      if (fresh.size) {
        void mutate(cur => (cur ? applyShelfDelta(cur, viewRef.current, fresh) : cur),
                    { revalidate: false });
      }
      setSaved(`✓ ${r.data.saved}`);
      window.setTimeout(() => setSaved(s => (s.startsWith('✓') ? '' : s)), 1500);
    } catch {
      // Put them back in the dirty set: the next edit flushes them again, so a blip —
      // a backend recreate mid-deploy is the usual one — costs nothing but a delay.
      ids.forEach(i => dirty.current.add(i));
      setSaved(t('data.pool.saveRetry'));
      // And CLEAR it. This message used to stay on screen forever while the tick after a
      // success faded, so one transient failure read as "nothing is saving" for the rest
      // of the session — including after everything had in fact saved.
      window.setTimeout(() => setSaved(s => (s === t('data.pool.saveRetry') ? '' : s)), 4000);
    }
  }, [mutate, t]);

  const scheduleSave = useCallback((id: number) => {
    dirty.current.add(id);
    if (timer.current) clearTimeout(timer.current);
    timer.current = window.setTimeout(flush, 250);
  }, [flush]);

  useEffect(() => () => { void flush(); }, [flush]);   // flush on unmount

  const setPct = useCallback((it: ShelfPoolItem, value: number | null) => {
    setVals(prev => ({ ...prev, [it.id]: value == null ? null : boundPct(value) }));
    setTouched(prev => (prev.has(it.id) ? prev : new Set(prev).add(it.id)));
    scheduleSave(it.id);
  }, [scheduleSave]);

  /** Step to the next crop. Nothing in this annotator scrolls — see the card ref below. */
  const advance = useCallback(() => {
    setFocus(f => Math.min(f + 1, Math.max(0, items.length - 1)));
  }, [items.length]);

  const move = useCallback((dir: 'left' | 'right' | 'up' | 'down') => {
    setFocus(f => {
      const n = items.length;
      if (!n) return 0;
      if (dir === 'right') return Math.min(n - 1, f + 1);
      if (dir === 'left') return Math.max(0, f - 1);
      if (dir === 'down') return Math.min(n - 1, f + cols);
      return Math.max(0, f - cols);
    });
  }, [items.length, cols]);

  // ── keyboard ───────────────────────────────────────────────────────────────
  // Digits are the fast path (0→0%, 5→50%, 9→90%); +/- nudge by one 5% step, so 9 then
  // + reaches 95 and 100 without leaving the home row. Backspace RETRACTS — the only way
  // to put a crop back in the queue after a misjudgement.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const tag = (e.target as HTMLElement | null)?.tagName;
      if (tag === 'TEXTAREA' || tag === 'INPUT' || tag === 'SELECT') return;
      if (e.key === 'ArrowRight') { e.preventDefault(); move('right'); return; }
      if (e.key === 'ArrowLeft') { e.preventDefault(); move('left'); return; }
      if (e.key === 'ArrowDown') { e.preventDefault(); move('down'); return; }
      if (e.key === 'ArrowUp') { e.preventDefault(); move('up'); return; }
      const it = items[focus];
      if (!it) return;
      const cur = valsRef.current[it.id];
      if (/^[0-9]$/.test(e.key)) {
        e.preventDefault(); setPct(it, Number(e.key) * 10); advance(); return;
      }
      if (e.key === '+' || e.key === '=') {
        e.preventDefault(); setPct(it, snapPct((cur ?? 0) + PERCENT_STEP)); return;
      }
      if (e.key === '-' || e.key === '_') {
        e.preventDefault(); setPct(it, snapPct((cur ?? 0) - PERCENT_STEP)); return;
      }
      if (e.key === 'Backspace' || e.key === 'Delete') {
        e.preventDefault(); setPct(it, null); return;
      }
      if (e.key === ' ' || e.key === 'Enter') {
        e.preventDefault();
        // Space means "I agree with what is shown". On an AI-labelled crop that IS the
        // review: re-sending the same percentage hands ownership to this labeller — the
        // backend credits AI-labelled and unjudged rows to whoever confirms them, on the
        // grounds that taking responsibility for a machine's guess is the work — and the
        // row becomes billable (the payout query counts source='user' only).
        //
        // Nothing is sent when there is no verdict to agree with, and nothing is sent for
        // a crop another human already judged to the same value: the backend would refuse
        // to move the credit anyway, so the write would be pure noise.
        const owned = it.source === 'user';
        if (cur != null && (!owned || cur !== it.percentage)) setPct(it, cur);
        advance();
        return;
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [focus, items, move, advance, setPct]);

  // No scrollIntoView anywhere in this annotator, deliberately. The mask editor sits
  // BELOW the grid and is where the work actually happens, so scrolling the focused card
  // into view drags the editor off the screen — with Enter, with the arrows, with every
  // key that moves the cursor. The labeller stays put and the editor swaps its contents
  // under them. (The table annotator does scroll, and should: there the grid is the only
  // surface and nothing sits below it to be dragged away.)

  const refresh = async () => {
    await flush(); setTouched(new Set()); accounted.current.clear(); await mutate();
  };
  /**
   * Apply a filter change after landing pending edits.
   *
   * Callers MUST read any event value synchronously and close over it: `flush()` is
   * awaited first, and during that await React re-renders the controlled <select>s back
   * to the current state value — so reading `e.target.value` afterwards yields the value
   * we came FROM, and the filter silently never moves.
   */
  const changeFilter = async (fn: () => void) => {
    await flush(); setTouched(new Set()); accounted.current.clear(); setPage(1); fn();
  };

  const startRetrain = async () => {
    const lines = [t('data.pool.retrainConfirm')];
    if (train) {
      lines.push('', `${t('data.pool.trainOn')}: ${train.labelled_now}`);
      lines.push(train.new_since_last == null
        ? t('data.pool.trainFirstPooled')
        : `${t('data.pool.trainNew')}: +${train.new_since_last}`);
      if (train.unlabelled) lines.push(`${t('data.pool.trainSkipped')}: ${train.unlabelled}`);
      // The distribution, not just the total: this pool is heavily skewed to full
      // shelves, and a retrain that cannot see the empty end is the failure mode.
      const low = (train.by_bucket['0-10'] ?? 0) + (train.by_bucket['10-20'] ?? 0);
      lines.push(`${t('data.shelf.lowBand')}: ${low}`);
    }
    if (!confirm(lines.join('\n'))) return;
    setBusy(true);
    try {
      await flush();
      const r = await shelfPoolRetrain();
      setSaved(`${t(r.data.already_running ? 'data.pool.retrainAlreadyRunning' : 'data.pool.retrainStarted')} ${r.data.run_id.slice(0, 8)}`);
      void mutateTrain();
      window.open(r.data.url, '_blank', 'noopener');
    } catch (e) {
      setSaved(`retrain failed: ${String((e as { message?: string })?.message ?? e).slice(0, 60)}`);
    } finally { setBusy(false); }
  };

  const removePhoto = async (it: ShelfPoolItem, action?: 'unlink' | 'delete') => {
    if (collection && collection !== 'none' && !action) { setPendingRemoval(it); return; }
    if (action !== 'unlink' && !confirm(t('data.annotate.deletePhotoConfirm'))) return;
    try {
      if (action === 'unlink') await unlinkCollection(collection, [it.id]);
      else await shelfPoolDelete([it.id]);
      setRemoved(prev => new Set(prev).add(it.id));
      setSaved(t(action === 'unlink' ? 'collection.unlinked' : 'data.annotate.photoDeleted'));
    } catch { setSaved('delete failed'); }
  };

  const zone = zones.find(z => z.zone_id === zoneId);
  const totalPages = data ? Math.max(1, Math.ceil(data.total / (data.page_size || PAGE_SIZE))) : 1;

  return (
    <div className="space-y-3">
      <CollectionRemoveDialog open={!!pendingRemoval} onClose={() => setPendingRemoval(null)} onChoose={action => {
        if (pendingRemoval) void removePhoto(pendingRemoval, action);
        setPendingRemoval(null);
      }} />
      {/* toolbar */}
      <div className="sticky top-0 z-10 flex flex-wrap items-center gap-3 rounded-md border bg-background/95 px-3 py-2 backdrop-blur">
        {/* Batch mode replaces the zone picker rather than sitting beside it: a batch
            spans every zone, so a zone filter alongside would only let a labeller narrow
            their own assignment back down to the thing the batch exists to spare them. */}
        {listIds.length ? (
          <span className="flex items-center gap-2 rounded-md border border-primary/40 bg-primary/10 px-2 py-1 text-sm">
            <Layers className="h-4 w-4" />
            {t('data.pool.listActive')}: {data?.total ?? listIds.length}
            <span className="text-xs text-muted-foreground">({t('data.pool.listHint')})</span>
            <button className="text-xs underline" onClick={exitList}>
              {t('data.pool.batchExit')}
            </button>
          </span>
        ) : batch ? (
          <span className="flex items-center gap-2 rounded-md border border-primary/40 bg-primary/10 px-2 py-1 text-sm">
            <Layers className="h-4 w-4" />
            {t('data.pool.batchActive')}: {data?.total ?? 0}
            <span className="text-xs text-muted-foreground">
              ({data?.facets.batch_zones?.length ?? 0} {t('data.pool.batchZones')} × ≤{batch})
            </span>
            <button className="text-xs underline" onClick={() => changeFilter(() => setBatch(0))}>
              {t('data.pool.batchExit')}
            </button>
          </span>
        ) : (
          <Select
            className="h-8" wrapperClassName="w-auto"
            value={zoneId ?? ''}
            onChange={e => { const v = e.target.value ? Number(e.target.value) : null; changeFilter(() => { setZoneId(v); setShelfId(null); }); }}
          >
            {collection && <option value="">{t('collection.allZones')}</option>}
            {zones.map(z => (
              <option key={z.zone_id} value={z.zone_id}>📍 {z.zone} ({z.total})</option>
            ))}
          </Select>
        )}

        {/* Sits beside the zone picker and acts on whatever it points at. The picker
            lists configured-but-empty zones at (0) precisely so one can be selected and
            then filled here; a finished run reloads it so the (0) becomes a real count. */}
        {!batch && !listIds.length && !collection && (
          <CollectCropsDialog
            zoneId={zoneId}
            zoneName={zone?.zone}
            api={SHELF_COLLECT_API}
            noObjectsKey="data.collect.noShelves"
            descriptionKey="data.collect.descriptionShelf"
            swrKey="shelf-collect"
            onCollected={() => { void reloadZones(); }}
          />
        )}

        {!batch && !collection && (
          <Tooltip content={t('data.pool.batchHint')}><span className="flex items-center gap-1 text-xs">
            <input
              type="number" min={1} max={500} value={batchSize}
              className="w-16 rounded-md border bg-background px-1.5 py-1"
              onChange={e => setBatchSize(Math.max(1, Math.min(500, Number(e.target.value) || 1)))}
            />
            <Button variant="outline" size="sm"
                    onClick={() => changeFilter(() => { setBatch(batchSize); setShelfId(null); })}>
              <Layers className="mr-1 h-4 w-4" /> {t('data.pool.batchStart')}
            </Button>
          </span></Tooltip>
        )}

        <div className="inline-flex items-center gap-1">
          <Button variant="outline" size="icon" className="h-7 w-7" title={t('data.annotate.zoomIn')}
                  disabled={cols <= MIN_COLS} onClick={() => setCols(c => Math.max(MIN_COLS, c - 1))}>
            <ZoomIn className="h-4 w-4" />
          </Button>
          <span className="w-4 text-center text-xs text-muted-foreground">{cols}</span>
          <Button variant="outline" size="icon" className="h-7 w-7" title={t('data.annotate.zoomOut')}
                  disabled={cols >= MAX_COLS} onClick={() => setCols(c => Math.min(MAX_COLS, c + 1))}>
            <ZoomOut className="h-4 w-4" />
          </Button>
        </div>

        {/* The view selector: unlabelled queue on top, labelled below. Every count is
            that view's OWN total — a number that moved when you clicked elsewhere could
            not be read as "work left". */}
        {!listIds.length && <div className="flex flex-col gap-1">
          {VIEW_ROWS.map((row, i) => (
            <div key={i} className="inline-flex overflow-hidden rounded-md border text-xs">
              {row.map(v => (
                <Tooltip key={v} content={v === 'cancelled' ? t('data.shelf.cancelledHint') : undefined}><button
                  className={`px-2.5 py-1 ${view === v
                    ? (v === 'cancelled'
                      ? 'bg-amber-100 text-amber-900'
                      : 'bg-secondary text-secondary-foreground')
                    : 'text-muted-foreground'}`}
                  onClick={() => changeFilter(() => setView(v))}
                >
                  {t(`data.annotate.filter.${viewKey(v)}`)} ({countFor(v)})
                </button></Tooltip>
              ))}
            </div>
          ))}
        </div>}

        {/* Dates exist only once something has been labelled, so this appears with the
            labelled views and not on the unlabelled queue. */}
        {!batch && !listIds.length && view !== 'unlabelled' && view !== 'cancelled' && view !== 'collected' && (
          <Tooltip content={t('data.pool.sortHint')}><span className="flex items-center gap-1 rounded-md border px-2 py-1 text-xs">
            <ArrowDownUp className="h-3.5 w-3.5 text-muted-foreground" />
            <span className="text-muted-foreground">{t('data.pool.sortLabel')}:</span>
            <Select
              wrapperClassName="w-auto"
              className={`h-auto border-0 bg-transparent py-0 pl-0 pr-6 text-xs shadow-none focus-visible:ring-0 ${sort ? 'font-medium text-primary' : ''}`}
              value={sort}
              onChange={e => { const v = e.target.value as Sort; changeFilter(() => setSort(v)); }}
            >
              <option value="">{t('data.shelf.sortDefault')}</option>
              <option value="annotated_desc">{t('data.pool.sortNewest')}</option>
              <option value="annotated_asc">{t('data.pool.sortOldest')}</option>
            </Select>
          </span></Tooltip>
        )}

        {/* shelf filter — counts respect the selected view */}
        {!listIds.length && <Select
          className="h-7 text-xs" wrapperClassName="w-auto"
          value={shelfId ?? 'all'}
          title={t('data.shelf.shelfFilter')}
          onChange={e => { const v = e.target.value; changeFilter(() => setShelfId(v === 'all' ? null : Number(v))); }}
        >
          <option value="all">
            {t('data.shelf.allShelves')} ({(data?.facets.shelves ?? []).reduce((n, o) => n + o.count, 0)})
          </option>
          {(data?.facets.shelves ?? []).filter(o => o.shelf_id != null).map(o => (
            <option key={o.shelf_id} value={o.shelf_id as number}>🗄 {o.shelf} ({o.count})</option>
          ))}
        </Select>}

        {/* First-pass AI labelling of the unlabelled queue. Offered only there, because
            that is the only place it has anything to do — and as a Dagster run, so an
            hour and a half of work is not tied to this tab staying open. */}
        {labelled === 'unlabelled' && (
          <Button size="sm" variant="outline" disabled={prelabelling}
                  title={t('data.mask.prelabelHint')}
                  onClick={async () => {
                    setPrelabelling(true);
                    try {
                      const r = await launchShelfPrelabel(100, batch ? null : zoneId);
                      setSaved(`${t('data.mask.prelabelStarted')} ${r.data.limit}`);
                      window.open(r.data.url, '_blank');
                    } catch {
                      setSaved(t('data.mask.prelabelFailed'));
                    } finally { setPrelabelling(false); }
                  }}>
            {prelabelling ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />
                          : <Sparkles className="mr-1.5 h-4 w-4" />}
            {t('data.mask.prelabel')}
          </Button>
        )}
        {/* Always visible, because it now changes the chip numbers from wherever you are
            standing — a control whose effect you can see but cannot find is worse than
            one more checkbox. */}
        <label className="inline-flex items-center gap-1.5 text-xs">
          <input type="checkbox" checked={hasMasks}
                 onChange={e => changeFilter(() => setHasMasks(e.target.checked))} />
          {t('data.mask.onlyWithMasks')}
        </label>

        <span className="text-xs text-muted-foreground">
          <kbd className="rounded bg-muted px-1">0…9</kbd> {t('data.shelf.keyDigits')} ·{' '}
          <kbd className="rounded bg-muted px-1">+/−</kbd> {PERCENT_STEP}% ·{' '}
          <kbd className="rounded bg-muted px-1">⌫</kbd> {t('data.shelf.keyClear')} ·{' '}
          <kbd className="rounded bg-muted px-1">Space</kbd> {t('data.shelf.keyAgree')} · ←→↑↓
        </span>

        <div className="ml-auto flex items-center gap-3">
          <span className={`text-xs ${saved.startsWith('✓') ? 'text-emerald-600' : 'text-amber-700'}`}>
            {saved}
          </span>
          <Tooltip content={t('data.pool.counterHint')}><span className="text-xs text-muted-foreground">
            {t('data.annotate.frame')} {items.length ? Math.min(focus + 1, items.length) : 0} / {items.length}
            {' · '}{t('data.pool.inFilter')} {data?.total ?? 0}
            {' · '}{t('data.annotate.left')} {zone?.todo ?? 0}
          </span></Tooltip>
          <Button variant="outline" size="sm" onClick={refresh} title={t('data.pool.refreshHint')}>
            <RefreshCw className="mr-1 h-4 w-4" /> {t('data.pool.refresh')}
          </Button>
          {train && (
            <Tooltip content={t('data.pool.trainHint')}><span className="text-[11px] text-muted-foreground">
              {train.new_since_last == null
                ? `${train.labelled_now} ${t('data.pool.trainReady')}`
                : `+${train.new_since_last} ${t('data.pool.trainSinceLast')}`}
            </span></Tooltip>
          )}
          <Button size="sm" disabled={busy} onClick={startRetrain} title={t('data.pool.retrainHint')}>
            <Play className="mr-1 h-4 w-4" /> {t('data.pool.retrain')}
          </Button>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-x-4 gap-y-1">
        <p className="text-xs text-muted-foreground">{t('data.shelf.hint')}</p>
        <Button variant={showUseful ? 'default' : 'outline'} size="sm" onClick={toggleUseful}
                title={t('data.shelf.usefulAreaHint')}>
          {showUseful ? <Eye className="mr-1 h-4 w-4" /> : <EyeOff className="mr-1 h-4 w-4" />}
          {t('data.shelf.usefulArea')}
          <span className="ml-1 tabular-nums opacity-80">
            {items.filter(i => i.useful_area).length}/{items.length}
          </span>
        </Button>
        {isLoading && <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />}
      </div>

      {/* The mask editor replaces the grid's percentage box entirely for crops SAM has
          been run on: correcting objects IS the labelling now, and the number follows.
          Shown only in the `masks` view, because everywhere else there is nothing to
          edit and an empty editor would be a puzzle. */}
      {items[focus] && (
        <ShelfMaskEditor
          item={{ id: items[focus].id, crop_url: items[focus].crop_url }}
          showUsefulArea={showUseful}
          onSaved={(pct) => {
            // Patch the CACHED RESPONSE rather than keeping a second set of books beside
            // it. Counting confirmations separately and adjusting the chips from that
            // drifted: Hills showed human 16 + AI 2, then 17 + 0 after a reload, and the
            // missing crop was never missing — the on-screen arithmetic was. One cache,
            // patched once, is the only way these two numbers stay equal.
            //
            // No refetch: it reorders the page, throws the cursor back to the first card
            // and makes the crop vanish from the view it was judged in.
            const id = items[focus].id;
            const wasAi = items[focus].source === 'ai';
            setVals(prev => ({ ...prev, [id]: pct }));
            void mutate(cur => {
              if (!cur) return cur;
              const labelled = { ...cur.facets.labelled };
              if (wasAi) { labelled.ai -= 1; labelled.human += 1; }
              return {
                ...cur,
                facets: { ...cur.facets, labelled },
                items: cur.items.map(i => (i.id === id
                  ? { ...i, source: 'user' as const, percentage: pct }
                  : i)),
              };
            }, { revalidate: false });
            // Step to the next crop once the save has LANDED, so Enter alone walks the
            // queue and nobody has to reach for the arrows between crops. Safe to move
            // the cursor now only because nothing here scrolls: the editor stays exactly
            // where it is on the screen and simply loads the next crop into itself.
            advance();
          }}
        />
      )}

      {/* Collapsed by default: a tool for the rare "what would this prompt find?", not
          part of labelling. It takes the focused crop when nothing is pasted, so the
          question can be asked about the picture actually on screen. */}
      <SamPromptBench
        item={items[focus] ? { id: items[focus].id, crop_url: items[focus].crop_url } : null}
      />

      {/* grid */}
      <div className="grid gap-3" style={{ gridTemplateColumns: `repeat(${cols}, minmax(0, 1fr))` }}>
        {items.map((it, i) => {
          const pct = vals[it.id];
          const isFocus = i === focus;
          return (
            <Tooltip key={it.id} content={`${it.shelf} · ${it.crop_file}`}><div
              onClick={() => setFocus(i)}
              className={`group relative cursor-pointer rounded-lg bg-white p-1 ${pctOutline(pct)} ${
                isFocus ? 'z-10 scale-[1.03] shadow-xl ring-[6px] ring-blue-600 ring-offset-2'
                  : 'opacity-95 hover:opacity-100'}`}
            >
              <Tooltip content={t('data.annotate.deletePhoto')}><button
                className="absolute left-1.5 top-1.5 z-10 rounded bg-rose-600/90 p-1 text-white opacity-0 transition group-hover:opacity-100 hover:bg-rose-700"
                aria-label={t('data.annotate.deletePhoto')}
                onClick={(e) => { e.stopPropagation(); removePhoto(it); }}
              >
                <Trash2 className="h-3.5 w-3.5" />
              </button></Tooltip>
              <CropWithUsefulArea src={it.crop_url} alt={it.crop_file}
                                  area={showUseful ? it.useful_area ?? null : null}
                                  className="block h-auto max-h-[600px] w-full rounded bg-black/5 object-contain" />

              {/* The slider is the mouse path; the keyboard path is above. Step is 5%
                  in both, so the two cannot produce values of different granularity. */}
              <div className="mt-1 flex items-center gap-2">
                <input
                  type="range" min={0} max={100} step={PERCENT_STEP}
                  value={pct ?? 0}
                  className={`h-1.5 w-full cursor-pointer ${pct == null ? 'opacity-40' : ''}`}
                  onChange={e => setPct(it, Number(e.target.value))}
                  onClick={e => e.stopPropagation()}
                />
                {pct == null ? (
                  <span className="shrink-0 rounded bg-amber-100 px-1 text-[11px] font-semibold uppercase text-amber-800">
                    {t('data.annotate.unset')}
                  </span>
                ) : (
                  <span className={`shrink-0 rounded px-1.5 text-[11px] font-semibold tabular-nums ${pctTone(pct)}`}>
                    {pct}%
                  </span>
                )}
                {/* The machine's own figure, beside the stored verdict rather than
                    instead of it. They differ on every crop that was labelled by an
                    earlier pass, and which is right is the question the labeller is here
                    to answer — hiding one of them would be answering it for them. */}
                {it.mask_percentage != null && (
                  <Tooltip content={t('data.mask.byMasksHint')}><span className="shrink-0 rounded bg-sky-100 px-1 text-[11px] tabular-nums text-sky-900">
                    {t('data.mask.byMasks')} {it.mask_percentage}%
                  </span></Tooltip>
                )}
              </div>

              <div className="mt-0.5 flex items-center justify-between gap-1 text-[11px] text-muted-foreground">
                <Tooltip content={it.camera ?? ''}><span className="truncate">
                  📷 {it.camera ?? `cam ${it.camera_id ?? '?'}`}
                </span></Tooltip>
                <span className="flex shrink-0 items-center gap-1">
                  {/* Who owns this verdict. Sits between the camera and the shelf because
                      that is where the eye already is, and it changes UNDER the cursor
                      when a crop is confirmed — the row stays put so a misclick can be
                      undone on the spot instead of being hunted for after a reload. */}
                  {it.source && (
                    <span className={`rounded px-1 font-semibold uppercase ${
                      it.source === 'user' ? 'bg-emerald-100 text-emerald-900'
                                           : 'bg-slate-200 text-slate-700'}`}>
                      {t(`data.mask.by.${it.source}`)}
                    </span>
                  )}
                  {it.origin === 'cancelled' && (
                    // Visible even without the filter on: knowing a crop is a hard
                    // negative changes how carefully it is judged.
                    <Tooltip content={t('data.shelf.cancelledHint')}><span className="rounded bg-amber-100 px-1 font-semibold uppercase text-amber-900">
                      {t('data.shelf.cancelledBadge')}
                    </span></Tooltip>
                  )}
                  {it.origin === 'legacy' && (
                    <Tooltip content={t('data.shelf.legacyHint')}><span className="rounded bg-slate-200 px-1 font-semibold uppercase text-slate-700">
                      legacy
                    </span></Tooltip>
                  )}
                  {it.zone && (
                    <Tooltip content={it.zone}><span className="truncate text-primary">📍 {it.zone}</span></Tooltip>
                  )}
                  <Tooltip content={it.shelf}><span className="max-w-[8rem] truncate">🗄 {it.shelf}</span></Tooltip>
                </span>
              </div>
            </div></Tooltip>
          );
        })}
      </div>

      {/* paging */}
      {data && data.total > (data.page_size || PAGE_SIZE) && (
        <div className="flex items-center justify-center gap-3 py-2">
          <Button variant="outline" size="sm" disabled={page <= 1}
                  onClick={async () => { await flush(); setTouched(new Set()); setPage(p => Math.max(1, p - 1)); }}>
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <span className="text-xs text-muted-foreground">{page} / {totalPages}</span>
          <Button variant="outline" size="sm" disabled={page >= totalPages}
                  onClick={async () => { await flush(); setTouched(new Set()); setPage(p => p + 1); }}>
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
      )}
      {!isLoading && !items.length && (
        <div className="py-10 text-center text-sm text-muted-foreground">{t('data.shelf.empty')}</div>
      )}
    </div>
  );
}
